import { inject, Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { where } from '@angular/fire/firestore';
import {
  CreateMemberPayload,
  GenerateMemberPaymentLinkPayload,
  GenerateMemberPaymentLinkResponse,
  Member,
} from '@models';
import { shareReplay, switchMap } from 'rxjs';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class MembersService {
  private readonly firestore = inject(FirestoreService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly functions = inject(Functions);

  private get col() {
    return this.tenantContext.collections.MEMBERS;
  }

  all$ = this.tenantContext.tenantId$.pipe(
    switchMap(() => this.firestore.listDocuments<Member>(this.col)),
    shareReplay(1),
  );

  byClub$(clubId: string) {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<Member>(
          this.col,
          where('clubId', '==', clubId),
        ),
      ),
      shareReplay(1),
    );
  }

  byUser$(userId: string) {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<Member>(
          this.col,
          where('userId', '==', userId),
        ),
      ),
      shareReplay(1),
    );
  }

  createMember(payload: CreateMemberPayload) {
    const callable = httpsCallable<
      CreateMemberPayload,
      { success: boolean; memberId: string }
    >(this.functions, 'createMember');
    return callable(payload);
  }

  generatePaymentLink(payload: GenerateMemberPaymentLinkPayload) {
    const callable = httpsCallable<
      GenerateMemberPaymentLinkPayload,
      GenerateMemberPaymentLinkResponse
    >(this.functions, 'generateMemberPaymentLink');
    return callable(payload);
  }
}
